// src/data/useCars.js
// Returns the fleet. Starts with the cars bundled in cars.js so the page is
// never empty, then swaps in /data/cars.json (written by the admin portal)
// when it exists. If that file is missing or invalid, the bundled list stays.
import { useEffect, useState } from 'react';
import bundled from './cars';

const CARS_URL = '/data/cars.json';
let cache = null;

function normalize(list) {
  return list
    .map((c, i) => ({ ...c, id: c.id || `car-${i}` }))
    .filter((c) => c && c.name && c.image)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

const onlyVisible = (list) => list.filter((c) => c.visible !== false);

// Every car including hidden ones, straight from the server (for /admin)
export async function loadAllCars() {
  try {
    const r = await fetch(CARS_URL, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('no cars.json');
    const data = await r.json();
    const list = Array.isArray(data) ? data : data && data.cars;
    if (Array.isArray(list) && list.length) return normalize(list);
  } catch (e) {
    /* fall through to the bundled fleet */
  }
  return normalize(bundled);
}

// After an admin save, show the new fleet without a reload
export function setCarsCache(list) {
  cache = onlyVisible(normalize(list));
}

export default function useCars() {
  const [cars, setCars] = useState(() => cache || onlyVisible(normalize(bundled)));

  useEffect(() => {
    if (cache) return undefined;
    let cancelled = false;
    fetch(CARS_URL, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('no cars.json'))))
      .then((data) => {
        const list = Array.isArray(data) ? data : data && data.cars;
        if (!Array.isArray(list) || list.length === 0) return;
        cache = onlyVisible(normalize(list));
        if (!cancelled) setCars(cache);
      })
      .catch(() => {
        /* keep the bundled fleet */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return cars;
}
